import { gql } from "@apollo/client";
import {
    createPagePagingActions,
    SortDirection,
    Table as AdminTable,
    TableQuery,
    useTableQuery,
    useTableQueryPaging,
    useTableQuerySort,
} from "@comet/admin";
import { ContentWrapper, FormattedDateWidthDateTime, useMemoizedTableVariables } from "@src/components";
import { StyledLink } from "@src/components/table/TableStyles";
import { type GQLUserAdminQuery, type GQLUserCamerasAdminQuery, type GQLUserCamerasAdminQueryVariables } from "@src/graphql.generated";
import { type FC, type PropsWithChildren, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";

const userCamerasQuery = gql`
    query UserCamerasAdmin($userId: ID!, $page: Int, $size: Int, $sort: SortInput) {
        camerasAdmin(userId: $userId, page: $page, size: $size, sort: $sort) {
            nodes {
                id
                name
                activatedAt
                hardwareInfo {
                    imei
                    msisdn
                }
            }
            totalCount
            totalPages
            nextPage
            previousPage
        }
    }
`;

const DevicesTable: FC<PropsWithChildren<GQLUserAdminQuery>> = ({ userAdmin }) => {
    const intl = useIntl();
    const pagingApi = useTableQueryPaging(1);
    const sortApi = useTableQuerySort({ columnName: "camera.name", direction: SortDirection.DESC });
    const addAdditionalQueryVariablesFn = useCallback(() => ({ userId: userAdmin.id, size: 5 }), [userAdmin.id]);

    const memoizedVariables = useMemoizedTableVariables<GQLUserCamerasAdminQueryVariables>({ pagingApi, sortApi, addAdditionalQueryVariablesFn });

    const { tableData, loading, error, api } = useTableQuery<GQLUserCamerasAdminQuery, GQLUserCamerasAdminQueryVariables>()(userCamerasQuery, {
        resolveTableData: ({ camerasAdmin }) => ({
            data: camerasAdmin.nodes,
            totalCount: camerasAdmin.totalCount,
            pagingInfo: createPagePagingActions(pagingApi, camerasAdmin),
        }),
        variables: memoizedVariables,
    });

    return (
        <ContentWrapper title={intl.formatMessage({ id: "reviermanager.user.cameras.title", defaultMessage: "Geräte" })}>
            <TableQuery api={api} loading={loading} error={error}>
                {tableData && (
                    <AdminTable
                        {...tableData}
                        sortApi={sortApi}
                        rowName={intl.formatMessage(
                            {
                                id: "reviermanager.pages.devices",
                                defaultMessage: "{count, plural, one {Gerät} other {Geräte}}",
                            },
                            { count: tableData.totalCount },
                        )}
                        columns={[
                            {
                                name: "camera.name",
                                sortable: true,
                                header: <FormattedMessage id="reviermanager.camera.name" defaultMessage="Name" />,
                                render: ({ name, id }) => <StyledLink to={`/devices/${id}/edit`}>{name}</StyledLink>,
                            },
                            {
                                name: "hardwareInfo.imei",
                                header: <FormattedMessage id="reviermanager.camera.imei" defaultMessage="IMEI" />,
                                render: ({ hardwareInfo }) => hardwareInfo.imei,
                            },
                            {
                                name: "hardwareInfo.msisdn",
                                header: <FormattedMessage id="reviermanager.camera.msisdn" defaultMessage="MSISDN" />,
                                render: ({ hardwareInfo }) => hardwareInfo.msisdn,
                            },
                            {
                                name: "camera.activatedAt",
                                sortable: true,
                                header: <FormattedMessage id="reviermanager.camera.table.activatedAt" defaultMessage="aktiviert am" />,
                                render: ({ activatedAt }) => activatedAt && <FormattedDateWidthDateTime value={activatedAt} />,
                            },
                        ]}
                    />
                )}
            </TableQuery>
        </ContentWrapper>
    );
};

export default DevicesTable;
